import { useRef, useEffect, useState } from "react";
import { useToast } from "../contexts/ToastContext";

import { TranscriberData } from "../hooks/useTranscriber";
import { formatAudioTimestamp } from "../utils/AudioUtils";

interface Props {
    transcribedData: TranscriberData | undefined;
}

export default function Transcript({ transcribedData }: Props) {
    const divRef = useRef<HTMLDivElement>(null);
    const { showToast } = useToast();
    const [copiedRecently, setCopiedRecently] = useState(false);

    const saveBlob = (blob: Blob, filename: string) => {
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = filename;
        link.click();
        URL.revokeObjectURL(url);
    };

    const exportTXT = () => {
        let chunks = transcribedData?.chunks ?? [];
        let text = chunks
            .map((chunk) => chunk.text)
            .join("")
            .trim();

        const blob = new Blob([text], { type: "text/plain" });
        saveBlob(blob, "transcript.txt");
        showToast('Transcript saved as TXT', 'success');
    };

    const exportJSON = () => {
        let jsonData = JSON.stringify(transcribedData?.chunks ?? [], null, 2);

        // post-process the JSON to make it more readable
        const regex = /(    "timestamp": )\[\s+(\S+)\s+(\S+)\s+\]/gm;
        jsonData = jsonData.replace(regex, "$1[$2 $3]");

        const blob = new Blob([jsonData], { type: "application/json" });
        saveBlob(blob, "transcript.json");
        showToast('Transcript saved as JSON', 'success');
    };

    const copyToClipboard = async () => {
        if (!transcribedData?.chunks) return;
        
        try {
            const text = transcribedData.chunks
                .map((chunk) => chunk.text)
                .join("")
                .trim();
                
            await navigator.clipboard.writeText(text);
            showToast('Transcript copied to clipboard', 'success');
            setCopiedRecently(true);
            setTimeout(() => setCopiedRecently(false), 2000);
        } catch (error) {
            showToast('Failed to copy transcript', 'error');
            console.error('Failed to copy:', error);
        }
    };

    // Scroll to the bottom when the component updates
    useEffect(() => {
        if (divRef.current) {
            const diff = Math.abs(
                divRef.current.offsetHeight +
                    divRef.current.scrollTop -
                    divRef.current.scrollHeight,
            );

            if (diff <= 64) {
                // We're close enough to the bottom, so scroll to the bottom
                divRef.current.scrollTop = divRef.current.scrollHeight;
            }
        }
    });

    // If there's no transcribed data, show a placeholder
    if (!transcribedData?.chunks || transcribedData.chunks.length === 0) {
        return (
            <div className="w-full p-6 bg-gray-50 dark:bg-gray-800/30 rounded-xl border border-gray-200 dark:border-gray-700 flex flex-col items-center justify-center text-center min-h-[250px] sm:min-h-[200px]">
                <div className="text-gray-400 dark:text-gray-500 mb-4 sm:mb-3 max-w-xs">
                    <svg className="w-14 h-14 sm:w-12 sm:h-12 mx-auto mb-3 sm:mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                    </svg>
                    <p className="text-base sm:text-sm font-medium">
                        {transcribedData?.isBusy 
                            ? "Transcribing your audio..." 
                            : "No transcript available yet"}
                    </p>
                    {transcribedData?.isBusy && (
                        <div className="animate-pulse mt-4 flex items-center justify-center">
                            <div className="w-2 h-2 bg-primary rounded-full mr-1"></div>
                            <div className="w-2 h-2 bg-primary rounded-full mr-1 animation-delay-200"></div>
                            <div className="w-2 h-2 bg-primary rounded-full animation-delay-400"></div>
                        </div>
                    )}
                    {!transcribedData?.isBusy && (
                        <p className="text-sm sm:text-xs mt-3 sm:mt-2 text-gray-400 dark:text-gray-500">
                            Record or upload audio to get started
                        </p>
                    )}
                </div>
            </div>
        );
    }

    return (
        <div className="w-full flex flex-col">
            {/* Transcript container */}
            <div
                ref={divRef}
                className="w-full flex flex-col max-h-[min(500px,60vh)] overflow-y-auto space-y-3 sm:space-y-2 custom-scrollbar rounded-xl bg-gray-50 dark:bg-gray-800/30 border border-gray-200 dark:border-gray-700 p-3 sm:p-4"
            >
                {transcribedData.chunks.map((chunk, i) => (
                    <div
                        key={`${i}-${chunk.text}`}
                        className="w-full flex flex-col sm:flex-row gap-3 sm:gap-4 rounded-lg p-4 sm:p-3 bg-white dark:bg-gray-800 border border-gray-200/80 dark:border-gray-700/80 hover:border-gray-300 dark:hover:border-gray-600 transition-colors duration-200 touch-manipulation relative group"
                    >
                        {/* Touch ripple effect for mobile */}
                        <span className="absolute inset-0 pointer-events-none opacity-0 group-active:opacity-100 group-active:animate-touch-ripple bg-primary/5 rounded-lg scale-0 origin-center"></span>
                        
                        <div className="text-xs text-gray-500 dark:text-gray-400 font-mono whitespace-nowrap bg-gray-100 dark:bg-gray-700 px-3 py-2 sm:px-2 sm:py-1 rounded sm:self-start">
                            {formatAudioTimestamp(chunk.timestamp[0])}
                        </div>
                        <div className="text-gray-700 dark:text-gray-200 flex-1 break-words text-base sm:text-sm">
                            {chunk.text}
                        </div>
                    </div>
                ))}
                
                {transcribedData.isBusy && (
                    <div className="flex items-center justify-center py-6 sm:py-4">
                        <div className="flex items-center space-x-3 sm:space-x-2 text-primary">
                            <svg className="animate-spin h-6 w-6 sm:h-5 sm:w-5" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            <span className="text-base sm:text-sm">Transcribing audio...</span>
                        </div>
                    </div>
                )}
            </div>

            {/* Actions bar */}
            {transcribedData && !transcribedData.isBusy && (
                <div className="w-full flex flex-wrap justify-end gap-3 sm:gap-2 mt-5 sm:mt-4">
                    <button
                        onClick={copyToClipboard}
                        className={`btn ${copiedRecently ? 'btn-success' : 'btn-primary'} flex items-center justify-center gap-2 py-3 sm:py-2 px-5 sm:px-4 rounded-xl sm:rounded-lg touch-manipulation relative group`}
                    >
                        {/* Touch ripple effect */}
                        <span className="absolute inset-0 pointer-events-none opacity-0 group-active:opacity-100 group-active:animate-touch-ripple bg-white/10 rounded-xl sm:rounded-lg scale-0 origin-center"></span>
                        
                        {copiedRecently ? (
                            <>
                                <svg className="w-5 h-5 sm:w-4 sm:h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                                Copied!
                            </>
                        ) : (
                            <>
                                <svg className="w-5 h-5 sm:w-4 sm:h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
                                </svg>
                                Copy
                            </>
                        )}
                    </button>
                    
                    <div className="relative group">
                        <button
                            className="btn btn-secondary flex items-center justify-center gap-2 py-3 sm:py-2 px-5 sm:px-4 rounded-xl sm:rounded-lg touch-manipulation relative"
                        >
                            <span className="absolute inset-0 pointer-events-none opacity-0 group-active:opacity-100 group-active:animate-touch-ripple bg-gray-200 dark:bg-gray-600 rounded-xl sm:rounded-lg scale-0 origin-center"></span>
                            
                            <svg className="w-5 h-5 sm:w-4 sm:h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                            </svg>
                            Export
                            <svg className="w-5 h-5 sm:w-4 sm:h-4 transition-transform duration-200 group-hover:rotate-180" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                        </button>
                        
                        <div className="absolute right-0 mt-2 w-44 sm:w-40 bg-white dark:bg-gray-800 rounded-xl sm:rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-1 hidden group-hover:block z-10">
                            <button
                                onClick={exportTXT}
                                className="w-full text-left px-5 py-3 sm:px-4 sm:py-2 text-base sm:text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                            >
                                Text (.txt)
                            </button>
                            <button
                                onClick={exportJSON}
                                className="w-full text-left px-5 py-3 sm:px-4 sm:py-2 text-base sm:text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                            >
                                JSON (.json)
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
